// INVDESIGN1-O1 (docs/build-briefs/INVDESIGN1-ORDER-ordering-screens.md; owner-approved mockups
// ~/Projects/SnipSip/handoff/mockups/invdesign1-7 to -10). The four ordering screens, drawn into one root:
//   What to order (7), the order draft with Send (8), Open orders (9) and Receive an order (10).
// This module draws them and wires the requests; the words and sums live in orderingModel.ts, tested without a DOM, and every
// amount, price, date and status comes from the server (orderingScreens.ts in src/domain), never worked out here beyond a
// line's quantity times its own price while a number is being edited.
//
// Text is set with textContent only (a product name is data). Laptop screens: a mouse and keyboard may be assumed, but every
// control is 44px or more and nothing is hover-only.

import {
  closeOrderShortRequest,
  getOpenOrders,
  getOrderingOrder,
  getPurchaseOrderPdf,
  getVendorOrderableItems,
  getWhatToOrder,
  markOrderSent,
  recordStockReceipt,
  saveOrderDraft,
  type OpenOrdersDto,
  type OrderableItemDto,
  type OrderingOrderDto,
  type OrderingOrderLineDto,
  type WhatToOrderDto,
  type WhatToOrderLineDto,
  type WhatToOrderVendorDto,
} from "./api.ts";
import { saveBlob } from "./consoleDownload.ts";
import { formatCartMoney as formatCents } from "./purchasingDisplay.ts";
import {
  arrivalText,
  countText,
  dayDate,
  dayName,
  lineDelivered,
  lineOrdered,
  lineTotalCents,
  monthDay,
  onOrderText,
  orderStatusChip,
  orderTitle,
  receiveResult,
  receivingOutcomeText,
  shortHeadline,
  stepQuantity,
  sumCents,
  summarizeReceiving,
  unitWord,
  vendorScheduleText,
  whatText,
  type ReceiveLineState,
  type StatusChip,
} from "./orderingModel.ts";
import type { Session } from "./session.ts";

export type OrderingView =
  | {
      kind: "what-to-order";
      /** INVDESIGN1-S2: the Inventory home's Edit amounts (focus this vendor's card) and Build order (save its suggested lines as
       * a draft and open it, exactly as Review and send does). */
      vendorId?: string;
      build?: boolean;
    }
  | { kind: "open-orders" }
  | { kind: "draft"; orderId: string }
  | { kind: "receive"; orderId: string };

export interface OrderingScreensDeps {
  root: HTMLElement;
  session: () => Session;
  hasPermission: (permissionId: string) => boolean;
  showToast: (message: string, variant: "success" | "danger") => void;
  openInventoryHub: () => void;
  openPastOrders: () => void;
  openOrderDetail: (orderId: string) => void;
  openVendors: () => void;
  openSetPar: () => void;
}

export interface OrderingScreensController {
  open(view: OrderingView): void;
  stop(): void;
}

const RECEIVE_DRAFT_PREFIX = "ord-receive-draft:";

type Attrs = {
  class?: string;
  testid?: string;
  text?: string;
  type?: string;
  disabled?: boolean;
  role?: string;
  inputmode?: string;
  value?: string;
  label?: string;
  hidden?: boolean;
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: readonly (Node | null)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs.class !== undefined) node.className = attrs.class;
  if (attrs.testid !== undefined) node.dataset["testid"] = attrs.testid;
  if (attrs.text !== undefined) node.textContent = attrs.text;
  if (attrs.type !== undefined) node.setAttribute("type", attrs.type);
  if (attrs.role !== undefined) node.setAttribute("role", attrs.role);
  if (attrs.inputmode !== undefined) node.setAttribute("inputmode", attrs.inputmode);
  if (attrs.label !== undefined) node.setAttribute("aria-label", attrs.label);
  if (attrs.value !== undefined) (node as unknown as HTMLInputElement).value = attrs.value;
  if (attrs.disabled === true) (node as unknown as HTMLButtonElement).disabled = true;
  if (attrs.hidden === true) node.hidden = true;
  for (const child of children) if (child !== null) node.append(child);
  return node;
}

function button(
  text: string,
  className: string,
  testid: string,
  onClick: () => void,
  options: { disabled?: boolean } = {},
): HTMLButtonElement {
  const node = el("button", {
    class: className,
    testid,
    text,
    type: "button",
    ...(options.disabled === true ? { disabled: true } : {}),
  });
  node.addEventListener("click", onClick);
  return node;
}

function chip(state: StatusChip, testid?: string): HTMLElement {
  return el("span", { class: `ord-chip ord-chip-${state.tone}`, text: state.text, ...(testid === undefined ? {} : { testid }) });
}

function money(cents: number | null): string {
  return cents === null ? "No price" : formatCents(cents);
}

function safeFileName(text: string): string {
  return text.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function message(body: { message?: string } | undefined, fallback: string): string {
  return body?.message !== undefined && body.message !== "" ? body.message : fallback;
}

export function createOrderingScreens(deps: OrderingScreensDeps): OrderingScreensController {
  let view: OrderingView = { kind: "what-to-order" };
  let generation = 0;

  // What to order
  let whatToOrder: WhatToOrderDto | null = null;
  let comingOrders: OpenOrdersDto | null = null;
  const amounts = new Map<string, number>();
  const cardErrors = new Map<string, string>();

  // Draft
  let draftOrder: OrderingOrderDto | null = null;
  let draftLines: { line: OrderingOrderLineDto; quantity: number }[] = [];
  let draftItems: OrderableItemDto[] = [];
  let draftDirty = false;
  let draftError: string | null = null;
  let draftBusy = false;

  // Open orders
  let openOrders: OpenOrdersDto | null = null;

  // Receive
  let receiveOrder: OrderingOrderDto | null = null;
  let receiveStates: ReceiveLineState[] = [];
  let receiveItems: OrderableItemDto[] = [];
  let decision: "later" | "close" | null = null;
  let receiveError: string | null = null;
  let receiveBusy = false;
  const postedKeys = new Set<string>();
  let shortClosed = false;

  const current = (): Session => deps.session();
  const stale = (token: number): boolean => token !== generation;

  function clearRoot(): void {
    deps.root.replaceChildren();
  }

  // ---------------------------------------------------------------- shell

  function subnav(): HTMLElement {
    const link = (text: string, testid: string, onClick: () => void, on: boolean): HTMLElement =>
      button(text, `ord-subnav-link${on ? " ord-subnav-on" : ""}`, testid, onClick);
    return el("nav", { class: "ord-subnav", testid: "ord-subnav", label: "Ordering" }, [
      link("Inventory", "ord-nav-inventory", () => deps.openInventoryHub(), false),
      link("Ordering", "ord-nav-ordering", () => api.open({ kind: "what-to-order" }), true),
      el("span", { class: "ord-subnav-gap" }),
      el("span", { class: "ord-subnav-label", text: "More" }),
      link("Open orders", "ord-nav-open-orders", () => api.open({ kind: "open-orders" }), view.kind === "open-orders"),
      link("Past orders", "ord-nav-past-orders", () => deps.openPastOrders(), false),
      link("Vendors", "ord-nav-vendors", () => deps.openVendors(), false),
      link("Set par", "ord-nav-set-par", () => deps.openSetPar(), false),
    ]);
  }

  function frame(...children: (Node | null)[]): void {
    clearRoot();
    deps.root.append(subnav(), el("div", { class: "ord-page", testid: `ord-page-${view.kind}` }, children));
  }

  function loading(text = "Loading"): void {
    frame(el("p", { class: "ord-muted", text: `${text}...`, testid: "ord-loading" }));
  }

  function failure(text: string): void {
    frame(el("p", { class: "ord-error", text, role: "alert", testid: "ord-load-error" }));
  }

  // -------------------------------------------------------- 7: What to order

  function amountOf(vendor: WhatToOrderVendorDto, line: WhatToOrderLineDto): number {
    return amounts.get(`${vendor.vendorId}:${line.liquorProductId}`) ?? Number(line.quantity);
  }

  function setAmount(vendor: WhatToOrderVendorDto, line: WhatToOrderLineDto, value: number): void {
    amounts.set(`${vendor.vendorId}:${line.liquorProductId}`, Math.max(0, Math.round(value)));
    renderWhatToOrder();
  }

  function stepper(
    quantity: number,
    unit: string | null,
    testid: string,
    onChange: (next: number) => void,
    options: { min?: number; max?: number } = {},
  ): HTMLElement {
    const min = options.min ?? 0;
    const input = el("input", {
      class: "ord-step-input",
      testid: `${testid}-input`,
      type: "text",
      inputmode: "numeric",
      value: String(quantity),
      label: "Quantity",
    });
    input.addEventListener("change", () => {
      const typed = Number(input.value.trim());
      onChange(Number.isFinite(typed) ? Math.min(Math.max(Math.round(typed), min), options.max ?? Infinity) : quantity);
    });
    const minus = button("−", "ord-step-button", `${testid}-minus`, () => onChange(stepQuantity(quantity, -1, min)));
    minus.setAttribute("aria-label", "One fewer");
    const plus = button("+", "ord-step-button", `${testid}-plus`, () =>
      onChange(Math.min(stepQuantity(quantity, 1, min), options.max ?? Infinity)),
    );
    plus.setAttribute("aria-label", "One more");
    return el("div", { class: "ord-step", testid }, [
      minus,
      input,
      el("span", { class: "ord-step-unit", text: unit === null ? "" : unitWord(unit, quantity) }),
      plus,
    ]);
  }

  function vendorCard(data: WhatToOrderDto, vendor: WhatToOrderVendorDto): HTMLElement {
    const total = sumCents(vendor.lines.map((line) => lineTotalCents(amountOf(vendor, line), line.unitCostCents)));
    const header = el("div", { class: "ord-card-head" }, [
      el("h2", { class: "ord-vendor-name", text: vendor.vendorName, testid: "ord-vendor-name" }),
      el("span", { class: "ord-chip ord-chip-warning", text: `${vendor.lowCount} low`, testid: "ord-low-chip" }),
      el("span", { class: "ord-muted ord-card-schedule", text: vendorScheduleText(vendor.schedule, data.today), testid: "ord-vendor-schedule" }),
      el("span", { class: "ord-card-total", text: formatCents(total), testid: "ord-vendor-total" }),
      button("Review and send", "btn-primary ord-review", "ord-review-and-send", () => void reviewAndSend(vendor)),
    ]);
    const head = el("thead", {}, [
      el("tr", {}, [
        el("th", { text: "Item" }),
        el("th", { class: "ord-num", text: "On hand" }),
        el("th", { class: "ord-num", text: "Par" }),
        el("th", { class: "ord-num", text: "Used last week" }),
        el("th", { text: "On order" }),
        el("th", { class: "ord-num", text: "Order" }),
        el("th", { class: "ord-num", text: "Each" }),
        el("th", { class: "ord-num", text: "Total" }),
      ]),
    ]);
    const parKeepsDecimal = vendor.lines.some((line) => line.par !== null && !Number.isInteger(Number(line.par)));
    const body = el(
      "tbody",
      {},
      vendor.lines.map((line) => {
        const quantity = amountOf(vendor, line);
        const onOrder = onOrderText(line.onOrder);
        return el("tr", { testid: "ord-line-row", class: "ord-row" }, [
          el("td", { class: "ord-name" }, [
            el("b", { text: line.productName, testid: "ord-line-name" }),
            line.sizeText === null ? null : el("span", { text: line.sizeText }),
          ]),
          el("td", { class: "ord-num", text: countText(line.onHand, true), testid: "ord-line-on-hand" }),
          el("td", { class: "ord-num", text: countText(line.par, parKeepsDecimal), testid: "ord-line-par" }),
          el("td", { class: "ord-num", text: countText(line.usedLastWeek, true), testid: "ord-line-used" }),
          el("td", {}, [
            onOrder === null
              ? el("span", { class: "ord-faint", text: "None", testid: "ord-line-on-order" })
              : el("span", { class: "ord-chip ord-chip-info", text: onOrder, testid: "ord-line-on-order" }),
          ]),
          el("td", { class: "ord-num" }, [
            stepper(quantity, line.orderUnit, "ord-line-amount", (next) => setAmount(vendor, line, next)),
          ]),
          el("td", { class: "ord-num", text: money(line.unitCostCents), testid: "ord-line-each" }),
          el("td", {
            class: "ord-num ord-strong",
            text: money(lineTotalCents(quantity, line.unitCostCents)),
            testid: "ord-line-total",
          }),
        ]);
      }),
    );
    const error = cardErrors.get(vendor.vendorId);
    return el("section", { class: "ord-card", testid: "ord-vendor-card" }, [
      header,
      error === undefined ? null : el("p", { class: "ord-error", text: error, role: "alert", testid: "ord-card-error" }),
      el("div", { class: "ord-table-wrap" }, [el("table", { class: "ord-table" }, [head, body])]),
    ]);
  }

  function summarizeUnits(lines: readonly { quantity: string; orderUnit: string | null }[]): string {
    const totals = new Map<string, number>();
    for (const line of lines) {
      const unit = (line.orderUnit ?? "unit").toLowerCase();
      totals.set(unit, (totals.get(unit) ?? 0) + Number(line.quantity));
    }
    const parts = [...totals].map(([unit, quantity]) => `${quantity} ${unitWord(unit, quantity)}`);
    return parts.length <= 1 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  }

  function comingCard(): HTMLElement | null {
    const sent = (comingOrders?.orders ?? [])
      .filter((order) => order.status === "placed" || order.status === "partially_received")
      .sort((a, b) => (a.arrivalDate ?? "9").localeCompare(b.arrivalDate ?? "9"))[0];
    if (sent === undefined) return null;
    const sentence = el("div", { class: "ord-coming-text", testid: "ord-coming-text" }, [
      el("b", { text: sent.vendorName }),
      el("span", { text: ` ${orderTitle(sent).toLowerCase()}, ${summarizeUnits(sent.lines)}${sent.arrivalDate === null ? "" : ", arrives "}` }),
      sent.arrivalDate === null ? null : el("b", { text: dayDate(sent.arrivalDate) }),
    ]);
    return el("section", { class: "ord-card ord-coming", testid: "ord-coming" }, [
      el("span", { class: "ord-eyebrow", text: "Coming" }),
      sentence,
      button("Open orders", "btn-secondary", "ord-coming-open-orders", () => api.open({ kind: "open-orders" })),
    ]);
  }

  function renderWhatToOrder(): void {
    const data = whatToOrder;
    if (data === null) return;
    const count =
      data.lastCount === null
        ? "From par and last week's use."
        : `From last count (${dayDate(data.lastCount.date)}${data.lastCount.byName === null ? "" : `, ${data.lastCount.byName}`}), par and last week's use.`;
    const cards = data.vendors.map((vendor) => {
      const card = vendorCard(data, vendor);
      card.dataset["vendorId"] = vendor.vendorId;
      if (view.kind === "what-to-order" && view.vendorId === vendor.vendorId) card.dataset["focused"] = "true";
      return card;
    });
    const empty =
      data.vendors.length === 0
        ? el("section", { class: "ord-card ord-pad", testid: "ord-nothing" }, [
            el("p", { class: "ord-strong", text: "Nothing to order right now." }),
            el("p", {
              class: "ord-muted",
              text:
                data.coveredByOpenOrders > 0
                  ? `${data.coveredByOpenOrders} ${data.coveredByOpenOrders === 1 ? "item is" : "items are"} below par and already on order.`
                  : "Every item is at or above par.",
            }),
          ])
        : null;
    const setup =
      data.needsSetup.length === 0
        ? null
        : el("section", { class: "ord-card ord-pad", testid: "ord-needs-setup" }, [
            el("p", { class: "ord-strong", text: `${data.needsSetup.length} ${data.needsSetup.length === 1 ? "item needs" : "items need"} setup before it can be suggested` }),
            el(
              "ul",
              { class: "ord-setup-list" },
              data.needsSetup.map((item) => el("li", { text: `${item.productName}: ${item.reason}` })),
            ),
          ]);
    frame(
      el("div", {}, [
        el("h1", { class: "ord-h1", text: "What to order" }),
        el("p", { class: "ord-muted", text: `${count} Change any amount. Items already on order are taken off.`, testid: "ord-subtitle" }),
      ]),
      ...cards,
      empty,
      setup,
      comingCard(),
    );
  }

  async function loadWhatToOrder(): Promise<void> {
    const token = ++generation;
    loading();
    const session = current();
    const [suggested, coming] = await Promise.all([
      getWhatToOrder(session.apiBaseUrl, session.tenantId),
      deps.hasPermission("purchasing.order.view")
        ? getOpenOrders(session.apiBaseUrl, session.tenantId)
        : Promise.resolve(undefined),
    ]);
    if (stale(token)) return;
    if (suggested.status !== 200) {
      failure(message(suggested.body, "What to order could not be loaded."));
      return;
    }
    whatToOrder = suggested.body.whatToOrder;
    comingOrders = coming !== undefined && coming.status === 200 ? coming.body.openOrders : null;
    renderWhatToOrder();
    const requested = view.kind === "what-to-order" ? view : undefined;
    if (requested?.vendorId !== undefined) {
      const vendorId = requested.vendorId;
      const wanted = whatToOrder.vendors.find((entry) => entry.vendorId === vendorId);
      if (wanted !== undefined && requested.build === true) {
        // Build order: the same save Review and send makes, on the suggested amounts; the draft opens when it is saved.
        view = { kind: "what-to-order" };
        await reviewAndSend(wanted);
      } else {
        deps.root
          .querySelector<HTMLElement>(`[data-vendor-id="${CSS.escape(vendorId)}"]`)
          ?.scrollIntoView({ block: "start" });
      }
    }
  }

  async function reviewAndSend(vendor: WhatToOrderVendorDto): Promise<void> {
    cardErrors.delete(vendor.vendorId);
    const lines = vendor.lines
      .map((line) => ({ liquorProductId: line.liquorProductId, quantity: amountOf(vendor, line) }))
      .filter((line) => line.quantity > 0)
      .map((line) => ({ liquorProductId: line.liquorProductId, quantity: String(line.quantity) }));
    if (lines.length === 0) {
      cardErrors.set(vendor.vendorId, "Order at least one item.");
      renderWhatToOrder();
      return;
    }
    const session = current();
    const saved = await saveOrderDraft(session.apiBaseUrl, session.tenantId, {
      vendorId: vendor.vendorId,
      lines,
      ...(vendor.draftOrderId === null ? {} : { replaceOrderId: vendor.draftOrderId }),
    });
    if (saved.status !== 201) {
      cardErrors.set(vendor.vendorId, message(saved.body, "The order could not be saved."));
      renderWhatToOrder();
      return;
    }
    api.open({ kind: "draft", orderId: saved.body.order.id });
  }

  // ------------------------------------------------ 8: build and send an order

  async function loadDraft(orderId: string): Promise<void> {
    const token = ++generation;
    loading();
    const session = current();
    const loaded = await getOrderingOrder(session.apiBaseUrl, session.tenantId, orderId);
    if (stale(token)) return;
    if (loaded.status !== 200) {
      failure(message(loaded.body, "The order could not be loaded."));
      return;
    }
    const order = loaded.body.order;
    if (order.status !== "draft") {
      deps.openOrderDetail(order.id);
      return;
    }
    const items = await getVendorOrderableItems(session.apiBaseUrl, session.tenantId, order.vendorId);
    if (stale(token)) return;
    draftOrder = order;
    draftItems = items.status === 200 ? items.body.items : [];
    draftLines = order.lines.map((line) => ({ line, quantity: Number(line.quantity) }));
    draftDirty = false;
    draftError = null;
    draftBusy = false;
    renderDraft();
  }

  function draftTotals(): { count: number; cents: number } {
    return {
      count: draftLines.length,
      cents: sumCents(draftLines.map((entry) => lineTotalCents(entry.quantity, entry.line.unitCostCents))),
    };
  }

  async function saveDraft(): Promise<boolean> {
    const order = draftOrder;
    if (order === null) return false;
    const lines = draftLines.filter((entry) => entry.quantity > 0);
    if (lines.length === 0) {
      draftError = "An order needs at least one item.";
      renderDraft();
      return false;
    }
    const session = current();
    draftBusy = true;
    const saved = await saveOrderDraft(session.apiBaseUrl, session.tenantId, {
      vendorId: order.vendorId,
      replaceOrderId: order.id,
      lines: lines.map((entry) => ({ liquorProductId: entry.line.liquorProductId, quantity: String(entry.quantity) })),
    });
    draftBusy = false;
    if (saved.status !== 201) {
      draftError = message(saved.body, "The order could not be saved.");
      renderDraft();
      return false;
    }
    const reloaded = await getOrderingOrder(session.apiBaseUrl, session.tenantId, order.id);
    if (reloaded.status === 200) {
      draftOrder = reloaded.body.order;
      draftLines = reloaded.body.order.lines.map((line) => ({ line, quantity: Number(line.quantity) }));
    }
    draftDirty = false;
    draftError = null;
    return true;
  }

  function addableItems(): OrderableItemDto[] {
    const present = new Set(draftLines.map((entry) => entry.line.liquorProductId));
    return draftItems.filter((item) => !present.has(item.liquorProductId) && item.unitCostCents !== null && item.orderUnit !== null);
  }

  function renderDraft(): void {
    const order = draftOrder;
    if (order === null) return;
    const totals = draftTotals();
    const rows = draftLines.map((entry, index) =>
      el("tr", { class: "ord-row", testid: "ord-draft-row" }, [
        el("td", { class: "ord-name" }, [
          el("b", { text: entry.line.productName, testid: "ord-draft-name" }),
          el("span", {
            text: [entry.line.sizeText, entry.line.orderUnit === null ? null : entry.line.orderUnit]
              .filter((part): part is string => part !== null)
              .join(" "),
          }),
        ]),
        el("td", { class: "ord-num" }, [
          stepper(
            entry.quantity,
            entry.line.orderUnit,
            "ord-draft-amount",
            (next) => {
              entry.quantity = next;
              draftDirty = true;
              renderDraft();
            },
            { min: 1 },
          ),
        ]),
        el("td", {
          class: "ord-num",
          text: entry.line.unitCostCents === null ? "No price" : `${formatCents(entry.line.unitCostCents)} each`,
          testid: "ord-draft-price",
        }),
        el("td", {
          class: "ord-num ord-strong",
          text: money(lineTotalCents(entry.quantity, entry.line.unitCostCents)),
          testid: "ord-draft-line-total",
        }),
        el("td", { class: "ord-remove-cell" }, [
          (() => {
            const remove = button("×", "ord-remove", "ord-draft-remove", () => {
              draftLines.splice(index, 1);
              draftDirty = true;
              renderDraft();
            });
            remove.setAttribute("aria-label", `Take ${entry.line.productName} off the order`);
            return remove;
          })(),
        ]),
      ]),
    );
    const addable = addableItems();
    const addSelect = el("select", { class: "ord-select", testid: "ord-add-item-select", label: "Item to add" });
    addSelect.append(el("option", { text: "Add an item", value: "" }));
    for (const item of addable) {
      const option = el("option", { text: `${item.productName}${item.sizeText === null ? "" : `, ${item.sizeText}`}`, value: item.liquorProductId });
      addSelect.append(option);
    }
    addSelect.addEventListener("change", () => {
      const picked = addable.find((item) => item.liquorProductId === addSelect.value);
      if (picked === undefined) return;
      draftLines.push({
        quantity: 1,
        line: {
          purchaseOrderLineId: "",
          liquorProductId: picked.liquorProductId,
          productName: picked.productName,
          sizeText: picked.sizeText,
          orderUnit: picked.orderUnit,
          bottlesPerOrderUnit: null,
          quantity: "1",
          unitCostCents: picked.unitCostCents,
          lineEstimateCents: picked.unitCostCents,
          receivedQuantity: "0",
          outstandingQuantity: "1",
        },
      });
      draftDirty = true;
      renderDraft();
    });
    const table = el("section", { class: "ord-card ord-draft-table", testid: "ord-draft-card" }, [
      el("div", { class: "ord-table-wrap" }, [
        el("table", { class: "ord-table" }, [
          el("thead", {}, [
            el("tr", {}, [
              el("th", { text: "Item and unit" }),
              el("th", { class: "ord-num", text: "Order" }),
              el("th", { class: "ord-num", text: "Price" }),
              el("th", { class: "ord-num", text: "Total" }),
              el("th", { text: "" }),
            ]),
          ]),
          el("tbody", {}, rows),
        ]),
      ]),
      el("div", { class: "ord-draft-foot" }, [
        addable.length === 0 ? el("span", { class: "ord-faint", text: "Every item this vendor sells is on the order." }) : addSelect,
        el("span", { class: "ord-muted", text: `${totals.count} ${totals.count === 1 ? "item" : "items"}`, testid: "ord-draft-count" }),
        el("span", { class: "ord-draft-total", text: formatCents(totals.cents), testid: "ord-draft-total" }),
      ]),
    ]);

    const schedule = order.schedule;
    const delivery = el("section", { class: "ord-card ord-pad", testid: "ord-delivery" }, [
      el("span", { class: "ord-eyebrow", text: "Delivery" }),
      el("p", { class: "ord-big", text: schedule === null ? "No schedule on file" : dayDate(schedule.arrivalDate), testid: "ord-delivery-day" }),
      el("p", {
        class: "ord-muted",
        testid: "ord-delivery-text",
        text:
          schedule === null
            ? `${order.vendorName} has no delivery days or cutoff on file.`
            : schedule.past
              ? `The cutoff for ${dayName(schedule.orderByDate)} ${schedule.orderByTime} has passed, so this arrives ${dayDate(schedule.arrivalDate)}.`
              : `${order.vendorName} delivers ${order.leadDays} ${order.leadDays === 1 ? "day" : "days"} after the order. Order by ${dayName(schedule.orderByDate)} ${schedule.orderByTime}.`,
      }),
    ]);
    const blockers = [...order.approvalBlockers];
    const canApprove = deps.hasPermission("purchasing.order.approve");
    const approvalNote =
      order.needsApprovalToSend && !canApprove
        ? "This order is at or above the approval limit, so a manager who can approve orders has to send it."
        : null;
    const sendChannel = order.sendChannel;
    const markDisabled = draftBusy || blockers.length > 0 || approvalNote !== null || totals.count === 0;
    const fileName = `${safeFileName(order.vendorName)}-order-${safeFileName(monthDay(order.createdAt.slice(0, 10)))}.pdf`;
    const send = el("section", { class: "ord-card ord-pad ord-send", testid: "ord-send" }, [
      el("span", { class: "ord-eyebrow", text: "Send to" }),
      el("p", {
        class: "ord-strong ord-send-to",
        text: order.contactName === null ? order.vendorName : `${order.contactName} at ${order.vendorName}`,
        testid: "ord-send-to",
      }),
      el("p", {
        class: "ord-muted",
        testid: "ord-send-reason",
        text: sendChannel.available
          ? "By email, with the purchase order attached. The order will read Sent, with the time."
          : sendChannel.reason,
      }),
      button(`Send order to ${order.vendorName}`, "btn-primary ord-send-main", "ord-send-order", () => undefined, {
        disabled: !sendChannel.available,
      }),
      button(`Download PDF (${fileName})`, "btn-secondary ord-send-pdf", "ord-download-pdf", () => void downloadPdf(fileName)),
      button("I ordered by phone, mark as sent", "ord-text-button", "ord-mark-sent", () => void markSent(), { disabled: markDisabled }),
      button("Save as draft", "ord-text-button", "ord-save-draft", () => void saveAsDraft(), { disabled: draftBusy }),
    ]);
    const notes: HTMLElement[] = [];
    if (blockers.length > 0) {
      notes.push(
        el("section", { class: "ord-card ord-pad ord-warn", testid: "ord-blockers" }, [
          el("p", { class: "ord-strong", text: "Fix before sending" }),
          el("ul", { class: "ord-setup-list" }, blockers.map((text) => el("li", { text }))),
        ]),
      );
    }
    if (approvalNote !== null) {
      notes.push(el("section", { class: "ord-card ord-pad ord-warn", testid: "ord-approval-note" }, [el("p", { text: approvalNote })]));
    }
    notes.push(
      el("section", { class: "ord-card ord-pad ord-muted", testid: "ord-draft-note" }, [
        el("p", { text: "Until you send or mark it, this order stays a draft and does not count as on order." }),
      ]),
    );
    frame(
      el("div", { class: "ord-title-row" }, [
        button("‹ What to order", "btn-secondary", "ord-back-to-what", () => void leaveDraft()),
        el("h1", { class: "ord-h1", text: `New order for ${order.vendorName}`, testid: "ord-draft-title" }),
        chip({ text: "Draft, not sent", tone: "neutral" }, "ord-draft-status"),
      ]),
      draftError === null ? null : el("p", { class: "ord-error", text: draftError, role: "alert", testid: "ord-draft-error" }),
      el("div", { class: "ord-split" }, [table, el("div", { class: "ord-side" }, [delivery, send, ...notes])]),
    );
  }

  async function leaveDraft(): Promise<void> {
    if (draftDirty && !(await saveDraft())) return;
    api.open({ kind: "what-to-order" });
  }

  async function saveAsDraft(): Promise<void> {
    if (draftDirty && !(await saveDraft())) return;
    deps.showToast("Saved as a draft.", "success");
    api.open({ kind: "open-orders" });
  }

  async function downloadPdf(fileName: string): Promise<void> {
    const order = draftOrder;
    if (order === null) return;
    if (draftDirty && !(await saveDraft())) return;
    const session = current();
    const result = await getPurchaseOrderPdf(session.apiBaseUrl, session.tenantId, order.id);
    if (result.status !== 200 || result.blob === undefined) {
      draftError = message(result, "The PDF could not be made.");
      renderDraft();
      return;
    }
    saveBlob(result.blob, fileName);
    deps.showToast("PDF downloaded.", "success");
  }

  async function markSent(): Promise<void> {
    const order = draftOrder;
    if (order === null) return;
    if (draftDirty && !(await saveDraft())) return;
    const session = current();
    draftBusy = true;
    renderDraft();
    const sent = await markOrderSent(session.apiBaseUrl, session.tenantId, order.id, crypto.randomUUID());
    draftBusy = false;
    if (sent.status !== 200) {
      draftError = message(sent.body, "The order could not be marked sent.");
      renderDraft();
      return;
    }
    deps.showToast(`Order marked as sent to ${order.vendorName}.`, "success");
    api.open({ kind: "open-orders" });
  }

  // ------------------------------------------------------- 9: open orders

  async function loadOpenOrders(): Promise<void> {
    const token = ++generation;
    loading();
    const session = current();
    const loaded = await getOpenOrders(session.apiBaseUrl, session.tenantId);
    if (stale(token)) return;
    if (loaded.status !== 200) {
      failure(message(loaded.body, "Open orders could not be loaded."));
      return;
    }
    openOrders = loaded.body.openOrders;
    renderOpenOrders();
  }

  function renderOpenOrders(): void {
    const data = openOrders;
    if (data === null) return;
    const canReceive = deps.hasPermission("inv.receipt.record");
    const rows = data.orders.map((order) => {
      const arrival = arrivalText(order, data.today);
      const isDraft = order.status === "draft";
      const action = isDraft
        ? button("Send", "btn-primary ord-row-action", "ord-open-send", () => api.open({ kind: "draft", orderId: order.id }))
        : canReceive
          ? button("Receive", "btn-secondary ord-row-action", "ord-open-receive", () => api.open({ kind: "receive", orderId: order.id }))
          : el("span");
      return el("tr", { class: "ord-row", testid: "ord-open-row" }, [
        el("td", { class: "ord-name" }, [
          el("b", { text: orderTitle(order), testid: "ord-open-number" }),
          el("span", { text: order.vendorName, testid: "ord-open-vendor" }),
        ]),
        el("td", {}, [chip(orderStatusChip(order, data.timeZone), "ord-open-status")]),
        el("td", { text: whatText(order), testid: "ord-open-what" }),
        el("td", { class: "ord-num ord-strong" }, [
          el("span", { text: money(order.estimatedTotalCents), testid: "ord-open-total" }),
          isDraft ? el("span", { class: "ord-est", text: " est." }) : null,
        ]),
        el("td", { testid: "ord-open-arrives" }, [
          el(arrival.rest.startsWith(", ") ? "b" : "span", { text: arrival.lead }),
          el("span", { text: arrival.rest }),
        ]),
        el("td", { class: "ord-action-cell" }, [action]),
      ]);
    });
    const open =
      data.orders.length === 0
        ? el("section", { class: "ord-card ord-pad", testid: "ord-open-empty" }, [
            el("p", { class: "ord-strong", text: "No open orders." }),
            el("p", { class: "ord-muted", text: "Orders you send, and drafts you save, show here." }),
          ])
        : el("section", { class: "ord-card", testid: "ord-open-card" }, [
            el("div", { class: "ord-table-wrap" }, [
              el("table", { class: "ord-table" }, [
                el("thead", {}, [
                  el("tr", {}, [
                    el("th", { text: "Order" }),
                    el("th", { text: "Status" }),
                    el("th", { text: "What" }),
                    el("th", { class: "ord-num", text: "Total" }),
                    el("th", { text: "Arrives" }),
                    el("th", { text: "" }),
                  ]),
                ]),
                el("tbody", {}, rows),
              ]),
            ]),
          ]);

    const glanceRows = data.glance.map((row) =>
      el("div", { class: "ord-glance-row", testid: "ord-glance-row" }, [
        el("span", { text: `${dayDate(row.date)}${row.kind === "draft" ? " (if sent)" : row.kind === "not_built" ? " (not built)" : ""}` }),
        el("span", {
          class: row.kind === "not_built" ? "ord-glance-muted" : "ord-strong",
          text:
            row.kind === "not_built"
              ? `${row.vendorName} order`
              : `${row.vendorName}, ${row.lineCount ?? 0} ${row.lineCount === 1 ? "line" : "lines"}`,
        }),
      ]),
    );
    const glance = el("section", { class: "ord-card ord-pad", testid: "ord-glance" }, [
      el("span", { class: "ord-eyebrow", text: "This week at a glance" }),
      glanceRows.length === 0 ? el("p", { class: "ord-muted", text: "Nothing is due this week." }) : el("div", { class: "ord-glance" }, glanceRows),
    ]);

    const closedRows = data.recentlyClosed.map((order) => {
      const shortText = order.shortLines
        .map((line) => `${line.shortQuantity} ${unitWord(line.orderUnit, Number(line.shortQuantity))} short, ${line.productName}`)
        .join("; ");
      return el("div", { class: "ord-closed-row", testid: "ord-closed-row" }, [
        el("div", { class: "ord-name ord-cl-name" }, [el("b", { text: orderTitle(order) }), el("span", { text: order.vendorName })]),
        (() => {
          const status = chip({ text: order.closedShort ? "Received, short" : "Received", tone: "success" }, "ord-closed-status");
          status.classList.add("ord-cl-chip");
          return status;
        })(),
        el("span", { class: "ord-muted ord-cl-short", text: shortText, testid: "ord-closed-short" }),
        el("span", { class: "ord-strong ord-cl-received", text: `${formatCents(order.receivedCents)} received`, testid: "ord-closed-received" }),
        el("span", { class: "ord-cl-arrived", text: order.arrivedDate === null ? "" : `Arrived ${dayDate(order.arrivedDate)}`, testid: "ord-closed-arrived" }),
        button("Open", "btn-secondary ord-cl-open", "ord-closed-open", () => deps.openOrderDetail(order.id)),
      ]);
    });
    const first = data.recentlyClosed[0];
    const footnote =
      first === undefined
        ? null
        : el("p", {
            class: "ord-muted ord-footnote",
            testid: "ord-closed-note",
            text:
              data.recentlyClosed.length === 1 && first.orderedCents !== first.receivedCents
                ? `Closed orders show what arrived, not what was ordered (${formatCents(first.orderedCents)} ordered).`
                : "Closed orders show what arrived, not what was ordered.",
          });
    const closed = el("section", { class: "ord-card ord-pad", testid: "ord-closed" }, [
      el("span", { class: "ord-eyebrow", text: "Recently closed" }),
      closedRows.length === 0 ? el("p", { class: "ord-muted", text: "No orders closed yet." }) : el("div", {}, closedRows),
      footnote,
    ]);

    frame(
      el("div", { class: "ord-title-row" }, [
        el("h1", { class: "ord-h1 ord-grow", text: "Open orders" }),
        deps.hasPermission("purchasing.cart.manage")
          ? button("What to order", "btn-primary", "ord-to-what-to-order", () => api.open({ kind: "what-to-order" }))
          : null,
      ]),
      open,
      el("div", { class: "ord-two" }, [glance, closed]),
    );
  }

  // ------------------------------------------------------ 10: receive an order

  function receiveDraftKey(orderId: string): string {
    return `${RECEIVE_DRAFT_PREFIX}${orderId}`;
  }

  function readReceiveDraft(orderId: string): { received: Record<string, number>; decision: "later" | "close" | null } | null {
    try {
      const raw = window.localStorage.getItem(receiveDraftKey(orderId));
      return raw === null ? null : (JSON.parse(raw) as { received: Record<string, number>; decision: "later" | "close" | null });
    } catch {
      return null;
    }
  }

  function writeReceiveDraft(orderId: string): void {
    try {
      const received: Record<string, number> = {};
      for (const state of receiveStates) received[state.line.purchaseOrderLineId] = state.received;
      window.localStorage.setItem(receiveDraftKey(orderId), JSON.stringify({ received, decision }));
    } catch {
      // A browser that cannot store a draft still receives; the draft is a convenience.
    }
  }

  function clearReceiveDraft(orderId: string): void {
    try {
      window.localStorage.removeItem(receiveDraftKey(orderId));
    } catch {
      // See writeReceiveDraft.
    }
  }

  async function loadReceive(orderId: string): Promise<void> {
    const token = ++generation;
    loading();
    const session = current();
    const loaded = await getOrderingOrder(session.apiBaseUrl, session.tenantId, orderId);
    if (stale(token)) return;
    if (loaded.status !== 200) {
      failure(message(loaded.body, "The order could not be loaded."));
      return;
    }
    const order = loaded.body.order;
    if (order.status === "draft") {
      api.open({ kind: "draft", orderId: order.id });
      return;
    }
    const items = await getVendorOrderableItems(session.apiBaseUrl, session.tenantId, order.vendorId);
    if (stale(token)) return;
    receiveOrder = order;
    receiveItems = items.status === 200 ? items.body.items : [];
    const saved = readReceiveDraft(order.id);
    receiveStates = order.lines
      .filter((line) => Number(line.outstandingQuantity) > 0)
      .map((line) => ({
        line,
        received: Math.min(saved?.received[line.purchaseOrderLineId] ?? Number(line.outstandingQuantity), Number(line.outstandingQuantity)),
        substitute: null,
      }));
    decision = saved?.decision ?? null;
    receiveError = null;
    receiveBusy = false;
    shortClosed = false;
    postedKeys.clear();
    renderReceive();
  }

  function substitutesFor(state: ReceiveLineState): OrderableItemDto[] {
    const unit = (state.line.orderUnit ?? "").toLowerCase();
    return receiveItems.filter(
      (item) =>
        item.liquorProductId !== state.line.liquorProductId &&
        (item.orderUnit ?? "").toLowerCase() === unit &&
        item.unitCostCents !== null,
    );
  }

  function renderReceive(): void {
    const order = receiveOrder;
    if (order === null) return;
    const orderedCents = sumCents(
      receiveStates.map((state) => lineTotalCents(lineOrdered(state), state.line.unitCostCents)),
    );
    const summary = summarizeReceiving(receiveStates, orderedCents);
    const arrival = order.schedule === null ? "" : `, ${dayDate(order.schedule.arrivalDate)}`;

    const lineRows = receiveStates.map((state) => {
      const result = receiveResult(state);
      const ordered = lineOrdered(state);
      const price =
        state.line.unitCostCents === null
          ? ""
          : `${formatCents(state.line.unitCostCents)} a ${(state.line.orderUnit ?? "unit").toLowerCase()}`;
      const shortAfter = ordered - lineDelivered(state);
      const sub = [price, lineDelivered(state) > 0 && shortAfter > 0 ? result.text : ""].filter((part) => part !== "").join(", ");
      return el("tr", { class: "ord-row", testid: "ord-receive-row" }, [
        el("td", { class: "ord-name" }, [
          el("b", { text: state.line.productName, testid: "ord-receive-name" }),
          el("span", { text: sub }),
        ]),
        el("td", { class: "ord-num", testid: "ord-receive-ordered" }, [
          el("b", { text: String(ordered) }),
          el("span", { text: ` ${unitWord(state.line.orderUnit, ordered)}` }),
        ]),
        el("td", { class: "ord-num" }, [
          stepper(
            state.received,
            state.line.orderUnit,
            "ord-receive-amount",
            (next) => {
              state.received = next;
              if (next > 0) state.substitute = null;
              writeReceiveDraft(order.id);
              renderReceive();
            },
            { min: 0, max: ordered },
          ),
        ]),
        el("td", {}, [chip(result, "ord-receive-result")]),
      ]);
    });

    const table = el("section", { class: "ord-card", testid: "ord-receive-card" }, [
      el("div", { class: "ord-table-wrap" }, [
        el("table", { class: "ord-table" }, [
          el("thead", {}, [
            el("tr", {}, [
              el("th", { text: "Line, in the vendor's unit" }),
              el("th", { class: "ord-num", text: "Ordered" }),
              el("th", { class: "ord-num", text: "Received" }),
              el("th", { text: "Result" }),
            ]),
          ]),
          el("tbody", {}, lineRows),
        ]),
      ]),
    ]);

    // A line nothing came for: what did they send instead?
    const substitutionCards = receiveStates
      .filter((state) => state.received === 0)
      .map((state) => {
        const candidates = substitutesFor(state);
        const ordered = lineOrdered(state);
        const select = el("select", { class: "ord-select", testid: "ord-substitute-select", label: "What they sent instead" });
        select.append(el("option", { text: "Nothing, it did not come", value: "" }));
        for (const item of candidates) {
          select.append(el("option", { text: item.productName, value: item.liquorProductId }));
        }
        select.value = state.substitute?.liquorProductId ?? "";
        select.addEventListener("change", () => {
          const picked = candidates.find((item) => item.liquorProductId === select.value);
          state.substitute =
            picked === undefined
              ? null
              : {
                  liquorProductId: picked.liquorProductId,
                  productName: picked.productName,
                  quantity: state.substitute?.quantity ?? ordered,
                  unitCostCents: picked.unitCostCents ?? 0,
                };
          renderReceive();
        });
        const controls: (Node | null)[] = [select];
        if (state.substitute !== null) {
          const substitute = state.substitute;
          const price = el("input", {
            class: "ord-price-input",
            testid: "ord-substitute-price",
            type: "text",
            inputmode: "decimal",
            value: (substitute.unitCostCents / 100).toFixed(2),
            label: "Price each",
          });
          price.addEventListener("change", () => {
            const typed = Number(price.value.replace(/[$,\s]/g, ""));
            if (Number.isFinite(typed) && typed >= 0) substitute.unitCostCents = Math.round(typed * 100);
            renderReceive();
          });
          controls.push(
            stepper(
              substitute.quantity,
              state.line.orderUnit,
              "ord-substitute-amount",
              (next) => {
                substitute.quantity = next;
                renderReceive();
              },
              { min: 1, max: ordered },
            ),
            el("label", { class: "ord-price-pill" }, [
              el("span", { text: "$" }),
              price,
              el("span", { text: ` a ${(state.line.orderUnit ?? "unit").toLowerCase()}` }),
            ]),
          );
        }
        return el("section", { class: "ord-card ord-pad ord-warn", testid: "ord-substitute-card" }, [
          el("p", { class: "ord-strong", text: `${state.line.productName}, ${ordered} ${unitWord(state.line.orderUnit, ordered)} not sent` }),
          el("p", { class: "ord-muted", text: "What did they send instead? This stock is added to the item you pick." }),
          el("div", { class: "ord-substitute-controls" }, controls),
        ]);
      });

    // Short lines: is the rest still coming?
    const decisionCard = summary.anyShort
      ? el("section", { class: "ord-card ord-pad", testid: "ord-short-card" }, [
          el("p", { class: "ord-strong", text: shortHeadline(summary.shortLines), testid: "ord-short-headline" }),
          el("p", { class: "ord-muted", text: "Is the rest still coming?" }),
          el("div", { class: "ord-decision" }, [
            button(
              "Rest is coming later",
              decision === "later" ? "btn-primary" : "btn-secondary",
              "ord-rest-later",
              () => {
                decision = "later";
                writeReceiveDraft(order.id);
                renderReceive();
              },
            ),
            button(
              "Rest is not coming, close short",
              decision === "close" ? "btn-primary" : "btn-secondary",
              "ord-rest-not-coming",
              () => {
                decision = "close";
                writeReceiveDraft(order.id);
                renderReceive();
              },
            ),
          ]),
        ])
      : null;

    const needsDecision = summary.anyShort && decision === null;
    const nothingToDo = summary.arrivedParts.length === 0 && !(summary.anyShort && decision === "close");
    const arrivedText = summary.arrivedParts.length === 0 ? "$0.00" : summary.arrivedParts.map((cents) => formatCents(cents)).join(" + ");
    const side = el("section", { class: "ord-card ord-pad ord-summary", testid: "ord-receive-summary" }, [
      el("span", { class: "ord-eyebrow", text: "Summary" }),
      el("div", { class: "ord-sum-row" }, [el("span", { text: "Ordered" }), el("b", { text: formatCents(summary.orderedCents), testid: "ord-sum-ordered" })]),
      el("div", { class: "ord-sum-row" }, [el("span", { text: "Arrived" }), el("b", { text: arrivedText, testid: "ord-sum-arrived" })]),
      el("div", { class: "ord-sum-row ord-sum-total" }, [
        el("span", { text: "Invoice total" }),
        el("b", { class: "ord-draft-total", text: formatCents(summary.invoiceCents), testid: "ord-sum-invoice" }),
      ]),
      el("p", {
        class: "ord-muted",
        testid: "ord-sum-outcome",
        text: receivingOutcomeText(summary, decision, (cents) => formatCents(cents)),
      }),
      receiveError === null ? null : el("p", { class: "ord-error", text: receiveError, role: "alert", testid: "ord-receive-error" }),
      button("Finish receiving", "btn-primary ord-send-main", "ord-finish-receiving", () => void finishReceiving(summary), {
        disabled: receiveBusy || needsDecision || nothingToDo,
      }),
      button("Save and finish later", "ord-text-button", "ord-save-later", () => {
        writeReceiveDraft(order.id);
        deps.showToast("Saved. Come back to finish receiving.", "success");
        api.open({ kind: "open-orders" });
      }),
    ]);

    frame(
      el("div", { class: "ord-title-row" }, [
        button("‹ Open orders", "btn-secondary", "ord-back-to-open", () => api.open({ kind: "open-orders" })),
        el("h1", { class: "ord-h1", text: `Receive ${orderTitle(order).toLowerCase()}`, testid: "ord-receive-title" }),
        el("span", { class: "ord-muted", text: `${order.vendorName}${arrival}` }),
      ]),
      el("div", { class: "ord-split ord-split-receive" }, [
        el("div", { class: "ord-stack" }, [table, ...substitutionCards, decisionCard]),
        side,
      ]),
    );
  }

  function receiptFor(
    line: OrderingOrderLineDto,
    quantity: number,
    liquorProductId: string,
    costCents: number | null,
    substitutedFor: string | null,
  ): Parameters<typeof recordStockReceipt>[2] | null {
    const pack = Number(line.bottlesPerOrderUnit ?? NaN);
    const unit = (line.orderUnit ?? "").toLowerCase();
    const base = {
      idempotencyKey: crypto.randomUUID(),
      liquorProductId,
      purchaseOrderLineId: line.purchaseOrderLineId,
      ...(substitutedFor === null ? {} : { substitutedForLiquorProductId: substitutedFor }),
    };
    if (pack > 1) {
      return {
        ...base,
        receivedOrderUnits: String(quantity),
        // CASECOST1: the case's cost as invoiced; the server divides it once, exactly, so 24 bottles value at the invoice.
        ...(costCents === null ? {} : { costPerOrderUnitCents: costCents }),
      };
    }
    if (unit === "keg" || unit === "bottle" || unit === "can") {
      return {
        ...base,
        receivedContainerCount: String(quantity),
        ...(costCents === null ? {} : { costPerContainerCents: costCents }),
      };
    }
    if (unit === "unit") {
      return { ...base, quantity: String(quantity), unit: "unit", ...(costCents === null ? {} : { unitCostCents: costCents }) };
    }
    return null;
  }

  async function finishReceiving(summary: ReturnType<typeof summarizeReceiving>): Promise<void> {
    const order = receiveOrder;
    if (order === null) return;
    const session = current();
    receiveBusy = true;
    receiveError = null;
    renderReceive();
    for (const state of receiveStates) {
      const jobs: { key: string; quantity: number; productId: string; cost: number | null; substitutedFor: string | null }[] = [];
      if (state.received > 0) {
        jobs.push({
          key: `${state.line.purchaseOrderLineId}:own`,
          quantity: state.received,
          productId: state.line.liquorProductId,
          cost: state.line.unitCostCents,
          substitutedFor: null,
        });
      }
      if (state.substitute !== null && state.substitute.quantity > 0) {
        jobs.push({
          key: `${state.line.purchaseOrderLineId}:sub`,
          quantity: state.substitute.quantity,
          productId: state.substitute.liquorProductId,
          cost: state.substitute.unitCostCents,
          substitutedFor: state.line.liquorProductId,
        });
      }
      for (const job of jobs) {
        if (postedKeys.has(job.key)) continue;
        const params = receiptFor(state.line, job.quantity, job.productId, job.cost, job.substitutedFor);
        if (params === null) {
          receiveBusy = false;
          receiveError = `${state.line.productName} cannot be received here. Receive it from Purchase orders.`;
          renderReceive();
          return;
        }
        const posted = await recordStockReceipt(session.apiBaseUrl, session.tenantId, params);
        if (posted.status !== 200 && posted.status !== 201) {
          receiveBusy = false;
          receiveError = `${state.line.productName}: ${message(posted.body, "the delivery could not be recorded.")}`;
          renderReceive();
          return;
        }
        postedKeys.add(job.key);
      }
    }
    if (summary.anyShort && decision === "close" && !shortClosed) {
      const closed = await closeOrderShortRequest(session.apiBaseUrl, session.tenantId, order.id, crypto.randomUUID());
      if (closed.status !== 200) {
        receiveBusy = false;
        receiveError = message(closed.body, "The order could not be closed short.");
        renderReceive();
        return;
      }
      shortClosed = true;
    }
    receiveBusy = false;
    clearReceiveDraft(order.id);
    deps.showToast(
      summary.anyShort && decision === "close"
        ? `Received, short, ${formatCents(summary.invoiceCents)}.`
        : `Received ${formatCents(summary.invoiceCents)}.`,
      "success",
    );
    api.open({ kind: "open-orders" });
  }

  // ------------------------------------------------------------------ api

  const api: OrderingScreensController = {
    open(next: OrderingView): void {
      view = next;
      deps.root.hidden = false;
      switch (next.kind) {
        case "what-to-order":
          amounts.clear();
          cardErrors.clear();
          void loadWhatToOrder();
          return;
        case "open-orders":
          void loadOpenOrders();
          return;
        case "draft":
          void loadDraft(next.orderId);
          return;
        case "receive":
          void loadReceive(next.orderId);
          return;
      }
    },
    stop(): void {
      generation += 1;
      whatToOrder = null;
      comingOrders = null;
      openOrders = null;
      draftOrder = null;
      draftLines = [];
      receiveOrder = null;
      receiveStates = [];
      amounts.clear();
      cardErrors.clear();
      postedKeys.clear();
      clearRoot();
    },
  };

  return api;
}
